/**
 * The appearance choice, and where it is kept.
 *
 * Both shells had a byte-identical copy of this, on the same key, with a
 * comment in each saying it belonged here once this package was free. It is,
 * and this is it. Two shells on two origins still have two localStorage jars —
 * the value cannot be shared across them — but the key, the event and the
 * behaviour now are, so a choice made in each reads the same in both.
 *
 * It sits beside `appearance-switcher.tsx` rather than inside it because a
 * module that exports both a component and plain functions cannot be
 * hot-reloaded as a component. The toast provider and its hook are split for
 * the same reason.
 */
export type Appearance = "system" | "light" | "dark";

const key = "magicweave.appearance";
const changeEvent = "magicweave:appearance-change";

/** The saved choice is the control state; system preference only resolves its colours. */
export function getAppearance(): Appearance {
  const saved = window.localStorage.getItem(key);
  return saved === "light" || saved === "dark" || saved === "system" ? saved : "system";
}

/** Keep every visible appearance control in sync, including changes from another tab. */
export function subscribeToAppearance(onChange: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key === key) {
      applyAppearance(getAppearance());
      onChange();
    }
  };
  window.addEventListener(changeEvent, onChange);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(changeEvent, onChange);
    window.removeEventListener("storage", onStorage);
  };
}

function applyAppearance(appearance: Appearance) {
  if (appearance === "system") {
    document.documentElement.removeAttribute("data-theme");
  } else {
    document.documentElement.dataset.theme = appearance;
  }
}

/** Apply an appearance choice without duplicating theme values outside CSS. */
export function setAppearance(appearance: Appearance) {
  applyAppearance(appearance);
  window.localStorage.setItem(key, appearance);
  window.dispatchEvent(new Event(changeEvent));
}

/** Restore the viewer's choice before the application paints. */
export function restoreAppearance() {
  applyAppearance(getAppearance());
}
